import { useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  reactorEconomyConfigSchema,
  ReactorEconomyConfigFormData,
} from '@olagg/validation-schemas';
import useAdminReactorEconomyConfigs from '@olagg/api-hooks/queries/useAdminReactorEconomyConfigs';
import OlaggForm from '../../components/Forms/OlaggForm';
import OlaGGFormInput from '../../components/Forms/OlaggFormInput';

// Mirrors the backend Quests::Reactor::Rate curve so the admin can preview the
// economic effect of the parameters before saving.
const computeRate = (d: number, rMin: number, rMax: number, dCap: number) => {
  if (!isFinite(d) || d <= 0) return 0;
  if (d <= 1) return rMin;
  const f = Math.min(1, Math.log(d) / Math.log(dCap));
  return rMin + (rMax - rMin) * f;
};

const timePerCredit = (rate: number) => {
  if (rate <= 0) return '—';
  const hours = 1 / rate;
  if (hours >= 168) return `${(hours / 168).toFixed(1)} sem`;
  if (hours >= 24) return `${(hours / 24).toFixed(1)} d`;
  if (hours >= 1) return `${hours.toFixed(2)} h`;
  return `${Math.round(hours * 60)} min`;
};

const PREVIEW_BALANCES = [0, 1, 10, 100, 1000, 10000];

const fmt = (v: number | string | undefined, digits = 6) =>
  v === undefined || v === null
    ? '—'
    : Number(v).toLocaleString('es-AR', { maximumFractionDigits: digits });

// Plain Box panel — using Chakra <Card> here resets the inherited text color to
// the (dark) body-text token, which is invisible on the team app's dark bg.
const panelProps = {
  bg: 'whiteAlpha.100',
  borderRadius: 'lg',
  p: 5,
  color: 'white',
} as const;

const ReactorEconomy = () => {
  const { configs, current, create } = useAdminReactorEconomyConfigs();

  const useFormMethods = useForm<ReactorEconomyConfigFormData>({
    resolver: yupResolver(reactorEconomyConfigSchema),
    mode: 'onChange',
    defaultValues: { rMin: 0.005952380952, rMax: 1, dCap: 10000, welcomeAmount: 30 },
  });
  const {
    control,
    reset,
    formState: { isSubmitting },
  } = useFormMethods;

  // Prefill the form with the currently-effective parameters once they load.
  useEffect(() => {
    if (current) {
      reset({
        rMin: Number(current.rMin),
        rMax: Number(current.rMax),
        dCap: Number(current.dCap),
        welcomeAmount: Number(current.welcomeAmount),
      });
    }
  }, [current, reset]);

  const watched = useWatch({ control });
  const rMin = Number(watched.rMin);
  const rMax = Number(watched.rMax);
  const dCap = Number(watched.dCap);

  const onSubmit = async (data: ReactorEconomyConfigFormData) => {
    try {
      await create(data);
    } catch {
      // errors are surfaced by the hook's toast
    }
  };

  if (!current) {
    return (
      <Center w="full" py={10}>
        <Spinner color="white" />
      </Center>
    );
  }

  return (
    <Center w="full" pb={8}>
      <VStack color="white" w="full" maxW="900px" spacing={6} align="stretch">
        <Box>
          <Heading my={2} color="white">
            Reactor — Economía de Créditos
          </Heading>
          <Text color="gray.300" fontSize="sm">
            Dos cosas distintas conviven acá. La <b>curva de producción</b> define a qué ritmo genera
            créditos un usuario según su saldo en USDC del Reactor. El <b>bono de bienvenida</b> es una
            entrega única que recibe cada usuario la primera vez, sin relación con la curva.
            Se guardan juntos en la misma versión.
          </Text>
        </Box>

        {/* Configuración vigente */}
        <Box {...panelProps}>
          <HStack justify="space-between" mb={3}>
            <Heading size="md" color="white">
              Configuración vigente
            </Heading>
            <Badge colorScheme="green">
              {current.effectiveFrom
                ? new Date(current.effectiveFrom).toLocaleString('es-AR')
                : 'Valores por defecto'}
            </Badge>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel color="gray.300">R_MIN (piso)</StatLabel>
              <StatNumber color="white">{fmt(current.rMin)}</StatNumber>
              <StatHelpText color="gray.400">
                créditos/hora — {timePerCredit(Number(current.rMin))} x crédito
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="gray.300">R_MAX (techo)</StatLabel>
              <StatNumber color="white">{fmt(current.rMax)}</StatNumber>
              <StatHelpText color="gray.400">
                créditos/hora — {timePerCredit(Number(current.rMax))} x crédito
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="gray.300">D_CAP</StatLabel>
              <StatNumber color="white">{fmt(current.dCap, 2)}</StatNumber>
              <StatHelpText color="gray.400">USDC donde la tasa llega al techo</StatHelpText>
            </Stat>
          </SimpleGrid>

          <Divider borderColor="whiteAlpha.300" my={4} />

          <Stat>
            <StatLabel color="gray.300">Bono de bienvenida</StatLabel>
            <StatNumber color="white">{fmt(current.welcomeAmount, 0)} créditos</StatNumber>
            <StatHelpText color="gray.400">
              Una sola vez por usuario — no se vuelve a otorgar aunque cambie el monto
            </StatHelpText>
          </Stat>
        </Box>

        {/* Editor de parámetros */}
        <Box {...panelProps}>
          <Heading size="md" mb={1} color="white">
            Nueva versión de parámetros
          </Heading>
          <Text color="gray.300" fontSize="sm" mb={4}>
            Al guardar se crea una nueva versión vigente desde ahora. Las cuentas que están produciendo
            se liquidan primero con los parámetros anteriores, así el tiempo ya transcurrido no se recalcula.
          </Text>

          <OlaggForm onSubmit={onSubmit} useFormMethods={useFormMethods} style={{ width: '100%' }}>
            <Text fontWeight={600} mb={3} color="white">
              Curva de producción
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <OlaGGFormInput
                controlName="rMin"
                label="R_MIN (créditos/hora)"
                description="Piso: cualquier saldo hasta 1 USDC produce a esta tasa"
                inputType="number"
                required
              />
              <OlaGGFormInput
                controlName="rMax"
                label="R_MAX (créditos/hora)"
                description="Techo: tasa máxima al alcanzar D_CAP"
                inputType="number"
                required
              />
              <OlaGGFormInput
                controlName="dCap"
                label="D_CAP (USDC)"
                description="Saldo donde la producción llega al techo"
                inputType="number"
                required
              />
            </SimpleGrid>

            {/* Preview de la curva con los valores del formulario */}
            <Box mt={2} mb={5}>
              <Text fontWeight={600} mb={2} color="white">
                Vista previa de la curva
              </Text>
              <TableContainer bg="whiteAlpha.50" borderRadius="md">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.300">Saldo (USDC)</Th>
                      <Th color="gray.300" isNumeric>
                        Tasa (créditos/hora)
                      </Th>
                      <Th color="gray.300">Tiempo x crédito</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {PREVIEW_BALANCES.map((d) => {
                      const r = computeRate(d, rMin, rMax, dCap);
                      return (
                        <Tr key={d} color="white">
                          <Td>
                            {d.toLocaleString('es-AR')}
                            {d === 10000 ? '+' : ''}
                          </Td>
                          <Td isNumeric>{isFinite(r) ? r.toFixed(4) : '—'}</Td>
                          <Td>{d === 0 ? 'no produce' : timePerCredit(r)}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            <Divider borderColor="whiteAlpha.300" mb={5} />

            <Text fontWeight={600} mb={1} color="white">
              Bono de bienvenida
            </Text>
            <Text color="gray.300" fontSize="sm" mb={3}>
              No afecta la curva de arriba. Solo cambia lo que reciben los usuarios que
              todavía no lo reclamaron: a quien ya se lo dieron no se le ajusta la diferencia.
            </Text>
            <Box maxW={{ base: 'full', md: '280px' }} mb={5}>
              <OlaGGFormInput
                controlName="welcomeAmount"
                label="Créditos de bienvenida"
                description="Entrega única por usuario. 0 desactiva el bono."
                inputType="number"
                required
              />
            </Box>

            <HStack justify="flex-end">
              <Button
                type="submit"
                colorScheme="purple"
                isLoading={isSubmitting}
                loadingText="Guardando..."
              >
                Guardar nueva versión
              </Button>
            </HStack>
          </OlaggForm>
        </Box>

        {/* Historial */}
        <Box>
          <Heading size="md" mb={2} color="white">
            Historial de versiones
          </Heading>
          <TableContainer {...panelProps} p={0}>
            <Table variant="striped" colorScheme="whiteAlpha" size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.300">Vigente desde</Th>
                  <Th color="gray.300" isNumeric>
                    R_MIN
                  </Th>
                  <Th color="gray.300" isNumeric>
                    R_MAX
                  </Th>
                  <Th color="gray.300" isNumeric>
                    D_CAP
                  </Th>
                  <Th color="gray.300" isNumeric>
                    Bienvenida
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {configs.length === 0 ? (
                  <Tr>
                    <Td colSpan={5}>
                      <Text color="gray.300">
                        Sin versiones guardadas (se usan los valores por defecto).
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  configs.map((c) => (
                    <Tr key={c.id} color="white">
                      <Td>{c.effectiveFrom ? new Date(c.effectiveFrom).toLocaleString('es-AR') : '—'}</Td>
                      <Td isNumeric>{fmt(c.rMin)}</Td>
                      <Td isNumeric>{fmt(c.rMax)}</Td>
                      <Td isNumeric>{fmt(c.dCap, 2)}</Td>
                      <Td isNumeric>{fmt(c.welcomeAmount, 0)}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <Divider borderColor="whiteAlpha.300" />
      </VStack>
    </Center>
  );
};

export default ReactorEconomy;
